import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { MapPin, Building2, ArrowRight, Phone, Loader2, Home, ChevronRight, Crown, BadgeCheck, Users } from 'lucide-react';
import { API } from '../utils/api';
import { CHILEAN_REGIONS, CHILEAN_CITIES_BY_REGION } from '../constants/chileanLocations';
import NotFoundPage from './NotFoundPage';

// Region metadata for SEO
var REGION_META = {
  'arica-y-parinacota': { name: 'Arica y Parinacota', region: 'Arica y Parinacota', query: 'Arica', description: 'Región más septentrional de Chile. Centros de rehabilitación en Arica y Putre para tratar adicciones a drogas, alcohol y pasta base.' },
  'tarapaca': { name: 'Tarapacá', region: 'Tarapacá', query: 'Tarapacá', description: 'Región del norte grande con centros especializados en Iquique, Alto Hospicio y Pozo Almonte. Tratamiento de adicciones a pasta base, cocaína y alcohol.' },
  'antofagasta': { name: 'Antofagasta', region: 'Antofagasta', query: 'Antofagasta', description: 'Región minera con alta incidencia de adicciones. Centros de rehabilitación en Antofagasta, Calama, Tocopilla y Mejillones.' },
  'atacama': { name: 'Atacama', region: 'Atacama', query: 'Atacama', description: 'Centros residenciales y ambulatorios en Copiapó, Vallenar y Chañaral. Tratamiento integral de adicciones en el norte de Chile.' },
  'coquimbo': { name: 'Coquimbo', region: 'Coquimbo', query: 'Coquimbo', description: 'Centros de rehabilitación en La Serena, Coquimbo, Ovalle e Illapel. Tratamientos ambulatorios, residenciales y comunidades terapéuticas.' },
  'valparaiso': { name: 'Valparaíso', region: 'Valparaíso', query: 'Valparaíso', description: 'Región de Valparaíso con más de 30 centros especializados en Viña del Mar, Valparaíso, Quilpué y otras ciudades.' },
  'metropolitana': { name: 'Metropolitana de Santiago', region: 'Metropolitana', query: 'Metropolitana', description: 'La región con mayor oferta de centros de rehabilitación de Chile. Más de 50 centros en Santiago, Puente Alto, Maipú, Las Condes, Providencia y comunas aledañas.' },
  'ohiggins': { name: "Libertador General Bernardo O'Higgins", region: "O'Higgins", query: "O'Higgins", description: "Centros de rehabilitación en Rancagua, San Fernando, Rengo y Machalí. Tratamiento integral de adicciones en la zona central." },
  'maule': { name: 'Maule', region: 'Maule', query: 'Maule', description: 'Centros en Talca, Curicó, Linares y Constitución. Programas de rehabilitación residencial y ambulatorio en el Maule.' },
  'nuble': { name: 'Ñuble', region: 'Ñuble', query: 'Ñuble', description: 'Región de Ñuble con centros de rehabilitación en Chillán, San Carlos y Coihueco. Tratamiento de adicciones y patología dual.' },
  'biobio': { name: 'Biobío', region: 'Biobío', query: 'Biobío', description: 'Más de 20 centros en Concepción, Talcahuano, Los Ángeles, Coronel y San Pedro de la Paz. Zona sur de Chile con fuerte oferta clínica.' },
  'la-araucania': { name: 'La Araucanía', region: 'La Araucanía', query: 'Araucanía', description: 'Centros en Temuco, Padre Las Casas, Villarrica y Pucón. Programas con enfoque intercultural y naturaleza.' },
  'los-rios': { name: 'Los Ríos', region: 'Los Ríos', query: 'Ríos', description: 'Centros en Valdivia, La Unión y Panguipulli. Tratamiento de adicciones en entornos naturales del sur de Chile.' },
  'los-lagos': { name: 'Los Lagos', region: 'Los Lagos', query: 'Lagos', description: 'Centros en Puerto Montt, Osorno, Castro y Puerto Varas. Rehabilitación con enfoque terapéutico en la Patagonia norte.' },
  'aysen': { name: 'Aysén', region: 'Aysén', query: 'Aysén', description: 'Centros de rehabilitación en Coyhaique, Puerto Aysén y Chile Chico. Apoyo especializado en la Patagonia chilena.' },
  'magallanes': { name: 'Magallanes y la Antártica', region: 'Magallanes', query: 'Magallanes', description: 'Centros en Punta Arenas, Puerto Natales y Porvenir. Rehabilitación en la zona más austral del país.' }
};

export default function RegionPage() {
  var params = useParams();
  var slug = params.slug;
  var meta = REGION_META[slug];
  var [centers, setCenters] = useState([]);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    if (!meta) { setLoading(false); return; }
    setLoading(true);
    axios.get(API + '/centers/public?region=' + encodeURIComponent(meta.query) + '&limit=50')
      .then(function(res) { setCenters(res.data.centers || []); })
      .catch(function() { setCenters([]); })
      .finally(function() { setLoading(false); });
  }, [slug]);

  if (!meta) {
    // Región no existe en nuestro catálogo → 404 con noindex (evita Soft 404 en GSC)
    return <NotFoundPage />;
  }

  var cities = CHILEAN_CITIES_BY_REGION[meta.region] || [];
  var verifiedCount = centers.filter(function(c) { return c.is_verified; }).length;
  var title = 'Centros de Rehabilitación en ' + meta.name + ' | ' + centers.length + ' verificados | SinAdicciones.org';
  var desc = meta.description + ' ' + centers.length + ' centros listados en el directorio.';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={'https://sinadicciones.org/region/' + slug} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={'https://sinadicciones.org/region/' + slug} />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={desc} />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": 'Centros de Rehabilitación en ' + meta.name,
          "description": desc,
          "url": 'https://sinadicciones.org/region/' + slug,
          "isPartOf": { "@type": "WebSite", "name": "SinAdicciones.org", "url": "https://sinadicciones.org" },
          "about": { "@type": "Place", "name": meta.name, "address": { "@type": "PostalAddress", "addressRegion": meta.region, "addressCountry": "CL" } }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
            { "@type": "ListItem", "position": 2, "name": "Regiones", "item": "https://sinadicciones.org/regiones" },
            { "@type": "ListItem", "position": 3, "name": meta.name }
          ]
        })}</script>
      
      <Header />
      <main className="pt-20 pb-16" data-testid="region-page">
        {/* Breadcrumbs */}
        <div className="bg-slate-900 border-b border-slate-800">
          <div className="max-w-6xl mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-slate-400">
              <Link to="/" className="hover:text-emerald-400 flex items-center gap-1"><Home className="w-3 h-3" />Inicio</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/regiones" className="hover:text-emerald-400">Regiones</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-slate-300">{meta.name}</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-b from-emerald-950/30 to-slate-950 border-b border-slate-800">
          <div className="max-w-6xl mx-auto px-4 py-12">
            <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium mb-3">
              <MapPin className="w-4 h-4" />
              <span>Región de {meta.name}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="region-title">
              Centros de Rehabilitación en {meta.name}
            </h1>
            <p className="text-lg text-slate-400 max-w-3xl leading-relaxed">{meta.description}</p>
            <div className="grid grid-cols-3 gap-4 mt-8 max-w-2xl">
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                <div className="text-3xl font-bold text-emerald-400">{centers.length}</div>
                <div className="text-xs text-slate-500 mt-1">Centros listados</div>
              </div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                <div className="text-3xl font-bold text-emerald-400">{verifiedCount}</div>
                <div className="text-xs text-slate-500 mt-1">Verificados</div>
              </div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                <div className="text-3xl font-bold text-emerald-400">{cities.length}</div>
                <div className="text-xs text-slate-500 mt-1">Ciudades cubiertas</div>
              </div>
            </div>
          </div>
        </section>

        {/* Centers list */}
        <section className="max-w-6xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold mb-6">Centros disponibles en {meta.name}</h2>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
          ) : centers.length === 0 ? (
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-8 text-center">
                <Building2 className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                <p className="text-slate-400 mb-4">Aún no tenemos centros listados en {meta.name}.</p>
                <Button asChild variant="outline" className="border-emerald-600 text-emerald-400 hover:bg-emerald-600/10">
                  <Link to="/centros-de-rehabilitacion-drogas-chile" data-testid="region-cta-all">Ver todos los centros de Chile</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="region-centers-grid">
              {centers.map(function(c) {
                return (
                  <Link key={c.center_id} to={'/centros/' + (c.slug || c.center_id)} className="group" data-testid={'region-center-' + c.center_id}>
                    <Card className="bg-slate-900/50 border-slate-800 hover:border-emerald-500/50 transition-all h-full">
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="font-semibold text-slate-50 group-hover:text-emerald-400 transition-colors line-clamp-2">{c.name}</h3>
                          {c.plan_level >= 3 && <Crown className="w-4 h-4 text-purple-400 flex-shrink-0" />}
                          {c.plan_level === 2 && <Crown className="w-4 h-4 text-amber-400 flex-shrink-0" />}
                        </div>
                        <div className="flex items-center gap-1 text-slate-400 text-sm mb-3">
                          <MapPin className="w-3 h-3" /><span>{c.city}</span>
                        </div>
                        {c.modality && (
                          <div className="text-xs text-slate-500 mb-3 capitalize">
                            {Array.isArray(c.modality) ? c.modality.slice(0, 3).join(' · ') : c.modality}
                          </div>
                        )}
                        <div className="flex items-center justify-between text-xs">
                          {c.is_verified && (
                            <span className="flex items-center gap-1 text-emerald-400"><BadgeCheck className="w-3 h-3" />Verificado</span>
                          )}
                          <span className="text-emerald-400 group-hover:translate-x-1 transition-transform inline-flex items-center gap-1 ml-auto">
                            Ver <ArrowRight className="w-3 h-3" />
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* Cities in region */}
        {cities.length > 0 && (
          <section className="max-w-6xl mx-auto px-4 py-8 border-t border-slate-800">
            <h2 className="text-xl font-bold mb-4">Ciudades de {meta.name}</h2>
            <div className="flex flex-wrap gap-2">
              {cities.map(function(city) {
                return (
                  <Link key={city} to={'/centros-de-rehabilitacion-drogas-chile?city=' + encodeURIComponent(city)} className="px-3 py-1.5 bg-slate-900 border border-slate-800 hover:border-emerald-500/50 rounded-full text-sm text-slate-300 hover:text-emerald-400 transition-colors">
                    {city}
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="max-w-6xl mx-auto px-4 py-12">
          <div className="bg-gradient-to-br from-emerald-900/30 to-slate-900 border border-emerald-500/20 rounded-2xl p-8 text-center">
            <Phone className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">¿Necesitas ayuda ahora mismo?</h2>
            <p className="text-slate-400 mb-6">Llama a Fonodrogas SENDA · Atención 24/7 gratuita y confidencial.</p>
            <Button asChild size="lg" className="bg-emerald-600 hover:bg-emerald-700">
              <a href="tel:1412" data-testid="region-fonodrogas">Llamar 1412</a>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export function RegionsHubPage() {
  var [counts, setCounts] = useState({});
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    async function load() {
      var results = {};
      await Promise.all(CHILEAN_REGIONS.map(async function(r) {
        try {
          var res = await axios.get(API + '/centers/public?region=' + encodeURIComponent(r.value) + '&limit=1');
          results[r.value] = res.data.total || 0;
        } catch (e) { results[r.value] = 0; }
      }));
      setCounts(results);
      setLoading(false);
    }
    load();
  }, []);

  function regionSlug(value) {
    return value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  var total = Object.values(counts).reduce(function(a, b) { return a + b; }, 0);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      
        <title>Centros de Rehabilitación por Región en Chile | 16 Regiones | SinAdicciones.org</title>
        <meta name="description" content={'Directorio nacional organizado por las 16 regiones de Chile. ' + total + ' centros de rehabilitación verificados para adicciones a drogas, alcohol, pasta base y más.'} />
        <link rel="canonical" href="https://sinadicciones.org/regiones" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/regiones" />
        <meta property="og:title" content="Centros de Rehabilitación por Región en Chile | 16 Regiones" />
        <meta property="og:description" content="Directorio nacional organizado por las 16 regiones de Chile. Centros verificados para adicciones a drogas, alcohol y pasta base." />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Centros de Rehabilitación por Región en Chile" />
        <meta name="twitter:description" content="Las 16 regiones de Chile en un solo directorio." />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
      
      <Header />
      <main className="pt-20 pb-16">
        <section className="bg-gradient-to-b from-emerald-950/30 to-slate-950 border-b border-slate-800">
          <div className="max-w-6xl mx-auto px-4 py-12 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">Centros de Rehabilitación por Región</h1>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">Encuentra centros de rehabilitación en cada una de las 16 regiones de Chile. {total > 0 ? total + ' centros verificados en todo el país.' : ''}</p>
          </div>
        </section>
        <section className="max-w-6xl mx-auto px-4 py-12">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="regions-grid">
              {CHILEAN_REGIONS.map(function(r) {
                var slug = regionSlug(r.value);
                var count = counts[r.value] || 0;
                return (
                  <Link key={r.value} to={'/region/' + slug} className="group" data-testid={'region-card-' + slug}>
                    <Card className="bg-slate-900/50 border-slate-800 hover:border-emerald-500/50 transition-all">
                      <CardContent className="p-5">
                        <MapPin className="w-5 h-5 text-emerald-400 mb-2" />
                        <h3 className="font-semibold text-slate-50 group-hover:text-emerald-400 transition-colors">{r.label}</h3>
                        <p className="text-sm text-slate-500 mt-1">{count} {count === 1 ? 'centro' : 'centros'}</p>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
